import { getNextRange, getNextRangeCoach } from "enefel/improvement";
import { getMalusFromMoral } from "enefel/moral";
import { BB2020Carac, getMaxGfi, PLAYER_CARAC } from "enefel/player";
import { getCareerFromPlayer } from "enefel/race";
import React from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineSmile } from "react-icons/ai";
import { CgSmileNeutral } from "react-icons/cg";
import { FaRegSmileBeam } from "react-icons/fa";
import { HiOutlineFaceSmile } from "react-icons/hi2";
import {
  PiSmileyAngryFill,
  PiSmileyNervousFill,
  PiSmileySadFill,
} from "react-icons/pi";
import styled from "styled-components/macro";
import palette from "../../styles/palette";
import CustomTooltip from "../Interface/Tooltip";
import PlayerCharac from "./PlayerCharac";

export const PopIcon = ({ value }) => {
  let icon;
  if (value >= 3) {
    icon = <FaRegSmileBeam />;
  } else if (value === 2) {
    icon = <AiOutlineSmile />;
  } else if (value === 1) {
    icon = <HiOutlineFaceSmile />;
  } else if (value === 0) {
    icon = <CgSmileNeutral />;
  } else if (value === -1) {
    icon = <PiSmileyNervousFill />;
  } else if (value === -2) {
    icon = <PiSmileySadFill />;
  } else {
    icon = <PiSmileyAngryFill />;
  }

  return (
    <CustomTooltip placement="top" title={value}>
      <span>{icon}</span>
    </CustomTooltip>
  );
};

const PlayerStatWrapper = styled.div`
  align-items: stretch;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 0.2em;
  text-align: center;

  h4 {
    color: ${(props) =>
      props.special ? "blue" : props.colorCarac ? props.colorCarac : null};
  }
`;

const MiniBadge = styled.span`
  color: blue;
  font-weight: bold;
  text-shadow: 1px 1px 0px ${palette.secondary.dark};
`;

const CategoryLetter = styled.span`
  cursor: help;
`;

type PlayerStatProps = {
  name: string;
  player?: any;
  prop?: any;
  currentValue?: any;
  special?: boolean;
  tooltipText?: string;
  value?: any;
  colorCarac?: string;
  children?: React.ReactNode;
  nameContent?: React.ReactNode;
  careerVersion?: any;
};

const PlayerStat: React.FC<PlayerStatProps> = React.memo(
  ({
    name,
    player,
    prop,
    currentValue,
    special,
    tooltipText,
    value = null,
    colorCarac = "red",
    children,
    nameContent,
    careerVersion: propCareerVersion,
  }) => {
    const careerVersion =
      propCareerVersion ||
      (player && prop ? getCareerFromPlayer(player)?.version : undefined);
    const displayValue =
      value !== null ? (
        BB2020Carac(value, prop, careerVersion)
      ) : (
        <PlayerCharac player={player} prop={prop} color={colorCarac} />
      );

    const nameElement = nameContent || name;

    return (
      <PlayerStatWrapper special={special} colorCarac={colorCarac}>
        {!tooltipText && <h4 color={colorCarac}>{nameElement}</h4>}
        {tooltipText && (
          <CustomTooltip placement="top" title={tooltipText}>
            <h4 color={colorCarac}>{nameElement}</h4>
          </CustomTooltip>
        )}
        <div>
          {children ? (
            <div
              style={{
                display: "flex",
                gap: "0.1em",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {children}
            </div>
          ) : (
            <>
              {prop === "pop" && (
                <PlayerStatWrapper special={special} colorCarac={colorCarac}>
                  <PopIcon value={value} />
                </PlayerStatWrapper>
              )}
              {prop === "pop" ? null : currentValue ===
                  (value === null ? player[prop] : value) ||
                currentValue == null ? (
                displayValue
              ) : (
                <>
                  {BB2020Carac(currentValue, prop, careerVersion)}/
                  {displayValue}
                </>
              )}
            </>
          )}
        </div>
      </PlayerStatWrapper>
    );
  }
);

const PlayerStatsWrapper = styled.div`
  margin: 0 0 0.5em;
  white-space: nowrap;
  text-align: ${(props) => (props.centered ? "center" : null)};
`;

type PlayerStatsProps = {
  player: any;
  current?: boolean;
  gfi?: boolean;
  centered?: boolean;
  forPlayer?: boolean;
  SPPCoach?: boolean;
  colorCarac?: string;
  career?: {
    normal?: string;
    double?: string;
    version?: any;
  };
  withCareerCategory?: boolean;
};

const PlayerStats: React.FC<PlayerStatsProps> = React.memo(
  ({
    player,
    current = true,
    gfi = false,
    centered = false,
    forPlayer = true,
    SPPCoach = true,
    colorCarac = undefined,
    career = null,
    withCareerCategory = true,
  }) => {
    const { t } = useTranslation();
    const { t: tSkill } = useTranslation("skill");
    const timeMalus = getMalusFromMoral(player.moral);
    const careerVersion =
      career?.version || getCareerFromPlayer(player)?.version;
    return (
      <div>
        <PlayerStatsWrapper centered={centered}>
          <PlayerStat
            colorCarac={colorCarac}
            tooltipText={t("t:MA Detail", "__MA Detail")}
            name={t("t:MA", "__MA")}
            player={player}
            prop={PLAYER_CARAC.MA}
            currentValue={current ? player.current_MA : null}
            careerVersion={careerVersion}
          />
          {gfi && (
            <PlayerStat
              colorCarac={colorCarac}
              tooltipText={t("t:GFI Detail", "__GFI Detail")}
              name={t("t:GFI", "__GFI")}
              value={getMaxGfi(player)}
              currentValue={current ? player.current_gfi : null}
              careerVersion={careerVersion}
            />
          )}
          <PlayerStat
            colorCarac={colorCarac}
            tooltipText={t("t:ST Detail", "__ST Detail")}
            name={t("t:ST", "__ST")}
            player={player}
            prop={PLAYER_CARAC.ST}
            currentValue={current ? player.current_ST : null}
            careerVersion={careerVersion}
          />
          <PlayerStat
            colorCarac={colorCarac}
            tooltipText={t("t:AG Detail", "__AG Detail")}
            name={t("t:AG", "__AG")}
            player={player}
            prop={PLAYER_CARAC.AG}
            currentValue={current ? player.current_AG : null}
            careerVersion={careerVersion}
          />
          <PlayerStat
            colorCarac={colorCarac}
            tooltipText={t("t:PA Detail", "__PA Detail")}
            name={t("t:PA", "__PA")}
            player={player}
            prop={PLAYER_CARAC.PA}
            currentValue={current ? player.current_PA : null}
            careerVersion={careerVersion}
          />
          <PlayerStat
            colorCarac={colorCarac}
            tooltipText={t("t:AV Detail", "__AV Detail")}
            name={t("t:AV", "__AV")}
            player={player}
            prop={PLAYER_CARAC.AV}
            currentValue={current ? player.current_AV : null}
            careerVersion={careerVersion}
          />
        </PlayerStatsWrapper>

        {withCareerCategory &&
          (() => {
            const careerData = career || getCareerFromPlayer(player);
            if (!careerData || (!careerData.normal && !careerData.double)) {
              return null;
            }
            return (
              <PlayerStatsWrapper centered={centered}>
                <PlayerStat
                  tooltipText={t("t:Normal", "__Normal")}
                  name=""
                  special={true}
                  nameContent={<MiniBadge>N</MiniBadge>}
                >
                  {(careerData.normal || "").split("").map((letter, index) => (
                    <CustomTooltip
                      key={letter + index}
                      placement="top"
                      title={tSkill(`skill:${letter}`, `__${letter}`)}
                    >
                      <CategoryLetter>{letter}</CategoryLetter>
                    </CustomTooltip>
                  ))}
                </PlayerStat>
                <PlayerStat
                  tooltipText={t("t:Double", "__Double")}
                  name=""
                  special={true}
                  nameContent={<MiniBadge>D</MiniBadge>}
                >
                  {(careerData.double || "").split("").map((letter, index) => (
                    <CustomTooltip
                      key={letter + index}
                      placement="top"
                      title={tSkill(`skill:${letter}`, `__${letter}`)}
                    >
                      <CategoryLetter>{letter}</CategoryLetter>
                    </CustomTooltip>
                  ))}
                </PlayerStat>
              </PlayerStatsWrapper>
            );
          })()}

        {forPlayer && (
          <PlayerStatsWrapper centered={centered}>
            <PlayerStat
              tooltipText={`${t("t:SSP Detail", "__SSP Detail")}: ${
                player.experience
              }/${getNextRange(player.experience)}`}
              name={t("t:SSP", "__SSP")}
              value={player.experience || 0}
              special={true}
            />
            {player.experienceCoach > 0 && SPPCoach && (
              <PlayerStat
                tooltipText={`${t("t:SPPCoach Detail", "__SPPCoach Detail")}: ${
                  player.experienceCoach
                }/${getNextRangeCoach(player.experienceCoach)}`}
                name={t("t:SPPCoach", "__SPPCoach")}
                value={player.experienceCoach || 0}
                special={true}
              />
            )}
            <PlayerStat
              tooltipText={t("t:POP Detail", "__POP Detail")}
              name={t("t:pop", "__pop")}
              value={player.pop || 0}
              special={true}
              prop={"pop"}
            />
            {timeMalus > 0 && (
              <PlayerStat
                tooltipText={t("t:Time malus Detail", "__Time malus Detail")}
                name={t("t:TIME", "__TIME")}
                value={`+${timeMalus}%`}
                special={true}
              />
            )}
          </PlayerStatsWrapper>
        )}
      </div>
    );
  }
);
export default PlayerStats;
